import React, { useCallback } from 'react';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import {useTranslation} from "react-i18next";
import SearchField from "../../form/SearchFiled";
import Container from "@material-ui/core/Container";
import {Button} from "@material-ui/core";
import {RotateLeft} from '@material-ui/icons';
import {
    COMMUNICATOR_PAGE_PATH,
    EVENT_UPDATE_PAGE_PATH,
    HOME_OVERVIEW_PAGE_PATH
} from "../../../layouts/navigation/Navigator";
import {shipmentSearch} from "../../../api/ShipmentEndpoint";
import {RR_STATUS} from "../../../constants/RRConstants";
import {useSnackbar} from "notistack";
import ShipmentList, {IShipment} from './ShipmentList';
import Loader from "../Loader";
import CloseBottomBar from '../CloseBottomBar';
import {useNavigate, useLocation} from "react-router-dom";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
            flex: 1,
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 80
        },
        loadMore: {
            padding: 10
        }
    }),
);

const LIMIT_QUERY = 10;

export type CommonShipmentsSearchParametersProps = {
    initQuery: string,
    initLoad: number,
    initScroll: number
}

export type CommonShipmentsPageProps = {
    detailsPagePath: string
    Icon: JSX.Element | JSX.Element[]
}

interface CommonShipmentsProps extends CommonShipmentsPageProps, CommonShipmentsSearchParametersProps {}

export const CommonShipmentsComponents = ({detailsPagePath, Icon, initQuery, initLoad, initScroll}: CommonShipmentsProps) => {

    const classes = useStyles();
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const [searchQuery, setSearchQuery] = React.useState(initQuery);
    const [shipments, setShipments] = React.useState<IShipment[] | ''>('');
    const [loading, setLoading] = React.useState(false);
    const [canLoadMore, setCanLoadMore] = React.useState(true);
    const [offset, setOffset] = React.useState(0);
    const navigate = useNavigate();
    const location = useLocation();


    const executeSearch = useCallback((query: string, load: number, redirectOnSingleResult: Boolean) => {
        setLoading(true);
        setOffset(load - LIMIT_QUERY);
        setCanLoadMore(true);

        navigate({search: `load=${load}&query=${query ? query : ""}&scroll=${initScroll && query === initQuery ? initScroll : ""}`});


        function redirectToDetailsView(spotID: string, uuid: string, shipmentId: number) {
            const pathname = (detailsPagePath === COMMUNICATOR_PAGE_PATH || detailsPagePath === EVENT_UPDATE_PAGE_PATH) ? `${detailsPagePath}/${uuid}/${shipmentId}` : `${detailsPagePath}/${uuid}`;
            navigate({
                pathname,
                search: `redirect=${encodeURIComponent(location.pathname + `?load=${load}&query=${query ? query : ""}&scroll=${spotID}`)}`
            });
        }


        shipmentSearch(query, 0, load).then(({data}) => {
            setLoading(false);
            const {status, response} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("core.server-error"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                setCanLoadMore(response && response.length >= LIMIT_QUERY);
                setShipments(response);

                if (redirectOnSingleResult && response && response.length === 1) {
                    const {spotID, shipmentId, uuid} = response[0];
                    redirectToDetailsView(spotID, uuid, shipmentId);
                }
            }
        })
    },[detailsPagePath, enqueueSnackbar, navigate, initQuery, initScroll, location.pathname, t]);

    React.useEffect(() => {
        executeSearch(initQuery, initLoad ? initLoad : LIMIT_QUERY, false);
    }, [executeSearch, initQuery, initLoad]);


    const executeLoadMore = () => {
        setLoading(true);
        const increasedOffset = offset + LIMIT_QUERY;
        navigate({search: `load=${(increasedOffset + LIMIT_QUERY)}&query=${searchQuery ? searchQuery : ""}&scroll=${initScroll ? initScroll : ""}`});

        setOffset(increasedOffset);
        setCanLoadMore(true);
        shipmentSearch(searchQuery, increasedOffset, LIMIT_QUERY).then(({data}) => {
            setLoading(false);
            const {status, response} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("core.server-error"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                setCanLoadMore(response && response.length === LIMIT_QUERY);
                const existingShipments = shipments ? shipments : [];
                setShipments([...existingShipments, ...response]);
            }
        })
    };


    return (<div className={classes.root}>

            <Container maxWidth="xs">
                <SearchField searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                             executeSearch={() => executeSearch(searchQuery, LIMIT_QUERY, true)}/>
            </Container>

            {shipments && <ShipmentList shipments={shipments} detailsPagePath={detailsPagePath} Icon={Icon}/>}

            {shipments && !loading && canLoadMore && <div className={classes.loadMore}>
                <Button
                    onClick={executeLoadMore}
                    variant="contained"
                    size={"small"}
                    color="primary"
                    startIcon={<RotateLeft/>}
                >
                    {t("core.load-more")}
                </Button>
            </div>}

            {loading && <Loader/>}

            {shipments && <CloseBottomBar path={HOME_OVERVIEW_PAGE_PATH}/>}

        </div>
    );
};


export default CommonShipmentsComponents;